import { Form, Link, Navigate, redirect, useActionData, useLoaderData, useNavigation, useParams, useRouteLoaderData, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import type { Amendment, ApprovalItem, Constraint, Decision, DecisionEvent, DecisionLineage } from "@edm/contracts";
import { FieldSkeleton, KeyValueSkeleton, Lines, Loading, times } from "../../components/skeletons.jsx";
import { Deferred } from "../../components/states.jsx";
import { translate, useI18n, type MessageKey } from "../../i18n/index.js";
import { api, isApiError, type TransitionTarget } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { saveFile } from "../../lib/download.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { ArrowLeftIcon, Badge, Button, Callout, Card, Disclosure, FormError, KeyValue, LinkButton, PageHeader, SaveBar, Skeleton, StatusBadge, Table, TextAreaField, TextField, useFormDirty } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

// Decision detail (ENG-10): lifecycle actions, lineage, constraints, amendments
// and the append-only history. Every rule (who may accept, what may be edited)
// is enforced by the core engine; this page only offers the actions and shows
// the engine's answer.

type DecisionRecord = { decision: Decision; lineage: DecisionLineage; history: DecisionEvent[]; amendments: Amendment[]; constraints: Constraint[] };

export function decisionLoader({ context, params }: LoaderFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const id = params.id!;
  const record = Promise.all([api.getDecision(id), api.decisionLineage(id), api.decisionHistory(id), api.listAmendments(id), api.listConstraints({ introducedBy: id })]).then(
    ([decision, lineage, history, amendments, constraints]) =>
      ({ decision, lineage, history: history.items, amendments: amendments.items, constraints: constraints.items }) as unknown as DecisionRecord,
  );
  return { record: deferred(record) };
}

const lines = (raw: string) =>
  raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

export async function decisionAction({ context, params, request }: ActionFunctionArgs) {
  const api = context.get(apiContext).workspace(context.get(workspaceContext).id);
  const id = params.id!;
  const form = await request.formData();
  const field = (k: string) => String(form.get(k) ?? "").trim();
  try {
    switch (field("intent")) {
      // Accepting and deprecating are votes under the workspace's approval rules
      // (D-0016). One vote may be enough — then this is the old one-click path —
      // or the request waits in Reviews for more. The page says which.
      case "transition": {
        const status = field("status") as TransitionTarget;
        if (status === "accepted") {
          const o = await api.approveDecision(id);
          const item = o.item as unknown as ApprovalItem;
          return o.applied ? { ok: true } : { notice: translate("decision.approvalRecorded", { have: item.approvals.length, required: item.required }) };
        }
        if (status === "deprecated") {
          // Asking to deprecate counts as the asker's own approval when the rules
          // allow it; otherwise the request simply waits for someone else.
          const r = await api.proposeRetirement(id, field("reason"));
          try {
            const o = await api.approveRetirement(id, r.number);
            if (o.applied) return { ok: true };
          } catch (err) {
            if (!isApiError(err, "validation_failed") && !isApiError(err, "forbidden")) throw err;
          }
          return { notice: translate("decision.retirementRequested", { ref: r.ref }) };
        }
        await api.transitionDecision(id, status, field("reason") || undefined);
        break;
      }
      case "propose-amendment":
        await api.proposeAmendment(id, {
          summary: field("summary"),
          ...(field("clarification") ? { clarification: field("clarification") } : {}),
          ...(lines(field("addAssumptions")).length ? { addAssumptions: lines(field("addAssumptions")) } : {}),
        } as Parameters<typeof api.proposeAmendment>[1]);
        break;
      case "decide-amendment": {
        if (field("accept") === "true") {
          const o = await api.approveAmendment(id, Number(field("number")));
          const item = o.item as unknown as ApprovalItem;
          if (!o.applied) return { notice: translate("decision.approvalRecorded", { have: item.approvals.length, required: item.required }) };
          break;
        }
        await api.decideAmendment(id, Number(field("number")), false, field("reason") || undefined);
        break;
      }
      default:
        return { error: translate("decision.unknownAction") };
    }
    return { ok: true };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function DecisionLinks({ ids, base }: { ids: string[]; base: string }) {
  const { t } = useI18n();
  if (ids.length === 0) return <span className="dt-subtle">{t("common.none")}</span>;
  return (
    <span className="dt-inline dt-gap-1">
      {ids.map((id) => (
        <Link key={id} to={`${base}/decisions/${id}`}>
          <Badge tone="neutral">{id}</Badge>
        </Link>
      ))}
    </span>
  );
}

function TransitionButton({ status, label, variant = "secondary", busy }: { status: TransitionTarget; label: string; variant?: "primary" | "secondary" | "ghost"; busy: boolean }) {
  return (
    <Form method="post">
      <input type="hidden" name="intent" value="transition" />
      <input type="hidden" name="status" value={status} />
      <Button type="submit" variant={variant} size="sm" loading={busy}>
        {label}
      </Button>
    </Form>
  );
}

function DownloadMarkdown({ d }: { d: Decision }) {
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        const file = await api.workspace(workspace.id).decisionMarkdown(d.id);
        saveFile(file.path.split("/").at(-1) ?? `${d.id}.md`, file.content, "text/markdown");
      }}
    >
      {t("decision.downloadMarkdown")}
    </Button>
  );
}

function Actions({ d, base, busy }: { d: Decision; base: string; busy: boolean }) {
  const { t } = useI18n();
  const edit = (
    <LinkButton to={`${base}/decisions/${d.id}/edit`} variant="ghost" size="sm">
      {t("decision.edit")}
    </LinkButton>
  );
  switch (d.status) {
    case "draft":
      return (
        <>
          {edit}
          <TransitionButton status="proposed" label={t("decision.propose")} variant="primary" busy={busy} />
        </>
      );
    case "proposed":
      return (
        <>
          {edit}
          <TransitionButton status="rejected" label={t("decision.reject")} busy={busy} />
          <TransitionButton status="accepted" label={t("decision.accept")} variant="primary" busy={busy} />
        </>
      );
    case "accepted":
      return (
        <>
          <DownloadMarkdown d={d} />
          <LinkButton to={`${base}/decisions/new?supersedes=${encodeURIComponent(d.id)}`} variant="secondary" size="sm">
            {t("decision.draftSuccessor")}
          </LinkButton>
        </>
      );
    default:
      return <DownloadMarkdown d={d} />;
  }
}

function History({ events }: { events: DecisionEvent[] }) {
  const { t, tOr, formatDateTime } = useI18n();
  return (
    <Card title={t("decision.history.title")} description={t("decision.history.description")}>
      <ol className="dt-stack dt-gap-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {events.map((e) => (
          <li key={e.seq} className="dt-inline dt-gap-2" style={{ flexWrap: "wrap" }}>
            <span className="dt-mono dt-subtle">r{e.revision}</span>
            <strong>{tOr(`event.${e.kind}`, e.kind.replaceAll("_", " "))}</strong>
            {e.kind === "transitioned" && (
              <span className="dt-inline dt-gap-1">
                {e.fromStatus && <StatusBadge status={e.fromStatus} />}→{e.toStatus && <StatusBadge status={e.toStatus} />}
              </span>
            )}
            {e.amendment && <Badge tone="neutral">A{e.amendment}</Badge>}
            <span className="dt-muted">{t("decision.history.actor", { actor: e.actorId, kind: tOr(`actor.${e.actorKind}`, e.actorKind) })}</span>
            {e.authority && (
              <Badge tone={e.authority === "human" ? "success" : "info"}>
                {e.authority === "policy" ? t("decision.history.policy", { id: e.policyId ?? "" }) : t("decision.history.human")}
              </Badge>
            )}
            {e.reason && <span className="dt-muted">“{e.reason}”</span>}
            <span className="dt-subtle">{formatDateTime(e.at)}</span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function Amendments({ d, amendments, busy }: { d: Decision; amendments: Amendment[]; busy: boolean }) {
  const { t } = useI18n();
  if (d.status !== "accepted" && amendments.length === 0) return null;
  return (
    <Card title={t("decision.amendments.title")} description={t("decision.amendments.description")}>
      {amendments.length > 0 && (
        <ul className="dt-stack dt-gap-3" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {amendments.map((a) => (
            <li key={a.number} className="dt-stack dt-gap-1">
              <span className="dt-inline dt-gap-2">
                <code>{a.ref}</code>
                <strong>{a.summary}</strong>
                <StatusBadge status={a.status} />
                <span className="dt-muted">{t("decision.amendments.by", { actor: a.proposedBy })}</span>
              </span>
              {a.clarification && <p className="dt-muted">{a.clarification}</p>}
              {a.addAssumptions?.map((x) => (
                <span key={x} className="dt-muted">
                  {t("decision.amendments.addedAssumption", { text: x })}
                </span>
              ))}
              {a.status === "proposed" && d.status === "accepted" && (
                <span className="dt-inline dt-gap-2">
                  {(["false", "true"] as const).map((accept) => (
                    <Form method="post" key={accept}>
                      <input type="hidden" name="intent" value="decide-amendment" />
                      <input type="hidden" name="number" value={a.number} />
                      <input type="hidden" name="accept" value={accept} />
                      <Button type="submit" size="sm" variant={accept === "true" ? "primary" : "secondary"} loading={busy}>
                        {accept === "true" ? t("decision.amendments.accept", { ref: a.ref }) : t("decision.amendments.reject", { ref: a.ref })}
                      </Button>
                    </Form>
                  ))}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {d.status === "accepted" && (
        <Disclosure summary={t("decision.amendments.propose")}>
          <Form method="post" className="dt-form" style={{ maxWidth: 560 }}>
            <input type="hidden" name="intent" value="propose-amendment" />
            <TextField name="summary" label={t("decision.amendments.summary")} maxLength={200} required />
            <TextAreaField name="clarification" label={t("decision.amendments.clarification")} rows={3} />
            <TextAreaField name="addAssumptions" label={t("decision.amendments.newAssumptions")} description={t("decision.amendments.onePerLine")} rows={3} />
            <div className="dt-form__actions">
              <Button type="submit" variant="secondary" loading={busy}>
                {t("decision.amendments.submit")}
              </Button>
            </div>
          </Form>
        </Disclosure>
      )}
    </Card>
  );
}

/** Back to the list, then the record: its facts, a few sections of prose, its history. The id is known from the URL. */
export function DecisionSkeleton() {
  const { t } = useI18n();
  const { workspaceSlug, id } = useParams();
  return (
    <Loading>
      <PageHeader
        eyebrow={
          <>
            <Link to={`/w/${workspaceSlug}/decisions`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
              <ArrowLeftIcon /> {t("decisions.title")}
            </Link>
            <span aria-hidden="true">/</span>
            <code>{id}</code>
            <Skeleton shape="badge" width={72} />
          </>
        }
        title={<Skeleton width={420} />}
        actions={
          <>
            <Skeleton shape="button-sm" width={56} />
            <Skeleton shape="button-sm" width={88} />
          </>
        }
      />
      <div className="dt-article">
        <Card>
          <KeyValueSkeleton
            keys={[
              t("decision.kv.scope"),
              t("decision.kv.origin"),
              t("decision.kv.revision"),
              t("decision.kv.createdBy"),
              t("decision.kv.approvedBy"),
              t("decision.kv.supersedes"),
              t("decision.kv.supersededBy"),
              t("decision.kv.dependsOn"),
            ]}
          />
        </Card>
        {times(2).map((i) => (
          <section key={i} className="dt-article__section">
            <h2>
              <Skeleton width={80} />
            </h2>
            <Lines count={3} />
          </section>
        ))}
        <Card title={t("decision.history.title")} description={t("decision.history.description")}>
          <ol className="dt-stack dt-gap-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {times(3).map((i) => (
              <li key={i} className="dt-inline dt-gap-2">
                <Skeleton width={20} />
                <Skeleton width={i ? 88 : 112} />
                <Skeleton width={200} />
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </Loading>
  );
}

export function DecisionRoute() {
  const { record } = useLoaderData<typeof decisionLoader>();
  return (
    <Deferred data={record} fallback={<DecisionSkeleton />}>
      {(record) => <DecisionPage {...record} />}
    </Deferred>
  );
}

function DecisionPage({ decision: d, lineage, history, amendments, constraints }: DecisionRecord) {
  const result = useActionData() as { error?: string; notice?: string } | undefined;
  const busy = useNavigation().state === "submitting";
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t, tOr, tNode } = useI18n();
  const base = `/w/${workspace.slug}`;
  const sections: Array<{ key: "context" | "problem" | "outcome" | "rationale"; body: string | undefined }> = [
    { key: "context", body: d.context },
    { key: "problem", body: d.problem },
    { key: "outcome", body: d.outcome },
    { key: "rationale", body: d.rationale },
  ];
  const filled = sections.filter((s) => s.body);
  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`${base}/decisions`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
              <ArrowLeftIcon /> {t("decisions.title")}
            </Link>
            <span aria-hidden="true">/</span>
            <code>{d.id}</code>
            <StatusBadge status={d.status} />
          </>
        }
        title={d.title}
        actions={<Actions d={d} base={base} busy={busy} />}
      />
      <div className="dt-article">
        <FormError message={result?.error} />
        {result?.notice && (
          <Callout tone="info">
            {result.notice} <Link to={`${base}/reviews`}>{t("decision.reviewsLink")}</Link>
          </Callout>
        )}
        {d.status === "superseded" && d.supersededBy.length > 0 && (
          <Callout tone="warning" title={t("decision.superseded.title")}>
            {tNode("decision.superseded.body", { links: <DecisionLinks ids={d.supersededBy} base={base} /> })}
          </Callout>
        )}
        <Card>
          <KeyValue
            items={[
              { key: t("decision.kv.scope"), value: d.scope || <span className="dt-subtle">{t("decision.kv.workspaceWide")}</span> },
              { key: t("decision.kv.origin"), value: tOr(`origin.${d.origin}`, d.origin) },
              { key: t("decision.kv.revision"), value: String(d.revision) },
              { key: t("decision.kv.createdBy"), value: d.createdBy.join(", ") || <span className="dt-subtle">{t("common.none")}</span> },
              { key: t("decision.kv.approvedBy"), value: d.approvedBy.join(", ") || <span className="dt-subtle">{t("common.none")}</span> },
              { key: t("decision.kv.supersedes"), value: <DecisionLinks ids={d.supersedes} base={base} /> },
              { key: t("decision.kv.supersededBy"), value: <DecisionLinks ids={d.supersededBy} base={base} /> },
              { key: t("decision.kv.dependsOn"), value: <DecisionLinks ids={d.dependsOn} base={base} /> },
            ]}
          />
        </Card>
        {filled.map((s) => (
          <section key={s.key} className="dt-article__section">
            <h2>{t(`decision.section.${s.key}` as MessageKey)}</h2>
            <p>{s.body}</p>
          </section>
        ))}
        {filled.length === 0 && <p className="dt-subtle">{t("decision.emptyBody")}</p>}
        {(d.alternatives?.length ?? 0) > 0 && (
          <section className="dt-article__section">
            <h2>{t("decision.alternatives")}</h2>
            <ul>
              {d.alternatives!.map((a) => (
                <li key={a.title}>
                  <strong>{a.title}</strong>
                  {a.summary ? ` — ${a.summary}` : ""}
                </li>
              ))}
            </ul>
          </section>
        )}
        {(d.assumptions?.length ?? 0) > 0 && (
          <section className="dt-article__section">
            <h2>{t("decision.assumptions")}</h2>
            <ul>
              {d.assumptions!.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </section>
        )}
        {(d.consequences?.length ?? 0) > 0 && (
          <section className="dt-article__section">
            <h2>{t("decision.consequences")}</h2>
            <ul>
              {d.consequences!.map((c) => (
                <li key={c.statement}>
                  <Badge tone={c.kind === "positive" ? "success" : c.kind === "negative" ? "warning" : "neutral"}>{tOr(`consequence.${c.kind}`, c.kind)}</Badge> {c.statement}
                </li>
              ))}
            </ul>
          </section>
        )}
        {(lineage.predecessors.length > 0 || lineage.successors.length > 0) && (
          <Card title={t("decision.lineage.title")}>
            <KeyValue
              items={[
                { key: t("decision.lineage.earlier"), value: <DecisionLinks ids={lineage.predecessors.map((n) => n.id)} base={base} /> },
                { key: t("decision.lineage.later"), value: <DecisionLinks ids={lineage.successors.map((n) => n.id)} base={base} /> },
              ]}
            />
          </Card>
        )}
        {constraints.length > 0 && (
          <Card title={t("decision.constraints.title")} padded={false}>
            <Table>
              <thead>
                <tr>
                  <th>{t("decision.constraints.th.id")}</th>
                  <th>{t("decision.constraints.th.statement")}</th>
                  <th>{t("decision.constraints.th.status")}</th>
                </tr>
              </thead>
              <tbody>
                {constraints.map((c) => (
                  <tr key={c.id}>
                    <td className="dt-mono dt-nowrap">{c.id}</td>
                    <td>
                      {c.statement}
                      {c.preservedBy && <span className="dt-muted">{t("decision.constraints.keptBy", { id: c.preservedBy })}</span>}
                      {c.replacedBy && <span className="dt-muted">{t("decision.constraints.replacedBy", { id: c.replacedBy })}</span>}
                    </td>
                    <td className="dt-nowrap">
                      <StatusBadge status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}
        {d.status === "accepted" && (
          <Disclosure summary={t("decision.deprecate.summary")}>
            <Form method="post" className="dt-form" style={{ maxWidth: 560 }}>
              <input type="hidden" name="intent" value="transition" />
              <input type="hidden" name="status" value="deprecated" />
              <TextField name="reason" label={t("decision.deprecate.reason")} required description={t("decision.deprecate.reasonHint")} />
              <div className="dt-form__actions">
                <Button type="submit" variant="secondary" loading={busy}>
                  {t("decision.deprecate.submit")}
                </Button>
              </div>
            </Form>
          </Disclosure>
        )}
        <Amendments d={d} amendments={amendments} busy={busy} />
        <History events={history} />
      </div>
    </>
  );
}

export function decisionEditLoader({ context, params }: LoaderFunctionArgs) {
  const decision = context.get(apiContext).workspace(context.get(workspaceContext).id).getDecision(params.id!) as Promise<Decision>;
  return { decision: deferred(decision) };
}

export async function decisionEditAction({ context, params, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const form = await request.formData();
  const field = (k: string) => String(form.get(k) ?? "").trim();
  const ids = (k: string) => field(k).split(/[\s,]+/).filter(Boolean);
  try {
    await context.get(apiContext).workspace(ws.id).updateDecision(params.id!, {
      revision: Number(field("revision")),
      title: field("title"),
      scope: field("scope"),
      context: field("context"),
      problem: field("problem"),
      outcome: field("outcome"),
      rationale: field("rationale"),
      supersedes: ids("supersedes"),
      dependsOn: ids("dependsOn"),
    });
    return redirect(`/w/${ws.slug}/decisions/${params.id}`);
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "conflict")) return { error: err.message };
    throw err;
  }
}

export function DecisionForm({ d, supersedes, submitLabel }: { d?: Decision | undefined; supersedes?: string | undefined; submitLabel: string }) {
  const result = useActionData() as { error?: string } | undefined;
  const nav = useNavigation();
  const { t } = useI18n();
  const form = useFormDirty();
  return (
    <Form ref={form.ref} method="post" className="dt-form" style={{ maxWidth: 640 }} onInput={form.check} onChange={form.check}>
      {d && <input type="hidden" name="revision" value={d.revision} />}
      <TextField name="title" label={t("decisionForm.title")} defaultValue={d?.title} placeholder={t("decisionForm.titlePlaceholder")} maxLength={200} required autoFocus />
      <TextField name="scope" label={t("decisionForm.scope")} defaultValue={d?.scope} placeholder={t("decisionForm.scopePlaceholder")} description={t("decisionForm.scopeHint")} />
      <TextAreaField name="context" label={t("decisionForm.context")} defaultValue={d?.context} placeholder={t("decisionForm.contextPlaceholder")} rows={4} />
      <TextAreaField name="problem" label={t("decisionForm.problem")} defaultValue={d?.problem} placeholder={t("decisionForm.problemPlaceholder")} rows={3} />
      <TextAreaField name="outcome" label={t("decisionForm.outcome")} defaultValue={d?.outcome} placeholder={t("decisionForm.outcomePlaceholder")} rows={4} />
      <TextAreaField name="rationale" label={t("decisionForm.rationale")} defaultValue={d?.rationale} placeholder={t("decisionForm.rationalePlaceholder")} rows={4} />
      <TextField
        name="supersedes"
        label={t("decisionForm.supersedes")}
        defaultValue={d ? d.supersedes.join(", ") : supersedes}
        placeholder="D-0007"
        description={t("decisionForm.supersedesHint")}
      />
      <TextField name="dependsOn" label={t("decisionForm.dependsOn")} defaultValue={d?.dependsOn.join(", ")} placeholder="D-0002, D-0003" />
      <FormError message={result?.error} />
      <SaveBar open={form.dirty} message={t("common.unsavedChanges")} saveLabel={submitLabel} busy={nav.state === "submitting"} />
    </Form>
  );
}

/** The decision form's fields, labelled, with nothing in them yet. */
export function DecisionFormSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <div className="dt-form" style={{ maxWidth: 640 }}>
        <FieldSkeleton label={t("decisionForm.title")} />
        <FieldSkeleton label={t("decisionForm.scope")} />
        <FieldSkeleton label={t("decisionForm.context")} multiline />
        <FieldSkeleton label={t("decisionForm.problem")} multiline />
        <FieldSkeleton label={t("decisionForm.outcome")} multiline />
        <FieldSkeleton label={t("decisionForm.rationale")} multiline />
        <FieldSkeleton label={t("decisionForm.supersedes")} />
        <FieldSkeleton label={t("decisionForm.dependsOn")} />
      </div>
    </Loading>
  );
}

function DecisionEditHeader() {
  const { t } = useI18n();
  const { workspaceSlug, id } = useParams();
  return (
    <PageHeader
      eyebrow={
        <Link to={`/w/${workspaceSlug}/decisions/${id}`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
          <ArrowLeftIcon /> {id}
        </Link>
      }
      title={t("decisionEdit.title")}
      description={t("decisionEdit.description")}
    />
  );
}

export function DecisionEditSkeleton() {
  return (
    <>
      <DecisionEditHeader />
      <Card>
        <DecisionFormSkeleton />
      </Card>
    </>
  );
}

export function DecisionEditRoute() {
  const { decision } = useLoaderData<typeof decisionEditLoader>();
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  return (
    <>
      <DecisionEditHeader />
      <Card>
        <Deferred data={decision} fallback={<DecisionFormSkeleton />}>
          {/* Only a draft or a proposal can be edited; anything else goes back to its page. */}
          {(d) => (d.status === "draft" || d.status === "proposed" ? <DecisionForm d={d} submitLabel={t("decisionEdit.submit")} /> : <Navigate to={`/w/${workspace.slug}/decisions/${d.id}`} replace />)}
        </Deferred>
      </Card>
    </>
  );
}
